import { useEffect, useRef, useState } from "react";
import { Play, X } from "lucide-react";
import { Button, EmptyState, Icon, IconButton, Spinner, Tag } from "@cntrl/ui";
import type { TutorialView } from "../../../app/hooks/useTutorials.js";
import { youTubeEmbedUrl, youTubeThumbnailUrl } from "../../../app/vendorLinks.js";
import styles from "./TutorialsList.module.scss";

export interface TutorialsListProps {
  tutorials: readonly TutorialView[] | undefined;
  isLoading: boolean;
  error: unknown;
  onRetry: () => void;
}

/**
 * Settings › Tutorials (ART-293): the admin console's how-to videos as a grid of thumbnails, each titled underneath,
 * in the admin's order. Shared by the band and org Settings — only the read differs, and each shell passes its own.
 *
 * Pressing a card plays it in a player above the grid (the grid keeps its order; a player spliced into the grid
 * would either leave a hole or move later cards ahead of it). ONE video at a time, and its player is the only
 * thing loaded from youtube.com: the cards are still thumbnails from i.ytimg.com, so an unopened list starts no
 * player, and closing the player — or picking another card — removes it, which is what stops the video.
 */
export function TutorialsList({ tutorials, isLoading, error, onRetry }: TutorialsListProps) {
  const [playingId, setPlayingId] = useState<string | null>(null);
  const stageRef = useRef<HTMLDivElement>(null);

  // Bring the player into view when a card further down the grid starts it.
  useEffect(() => {
    if (playingId) stageRef.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [playingId]);

  if (isLoading) {
    return (
      <div className={styles.center} role="status" aria-live="polite">
        <Spinner size={20} label="Loading tutorials…" />
      </div>
    );
  }
  if (error) {
    return (
      <EmptyState
        title="Couldn't load the tutorials"
        description={error instanceof Error ? error.message : "Please try again."}
        action={<Button onClick={onRetry}>Retry</Button>}
      />
    );
  }
  if (!tutorials?.length) {
    return <EmptyState title="No tutorials yet" description="Videos showing how to use Artist CNTRL will appear here." />;
  }

  // A tutorial that left the list (a refetch) closes its player rather than playing something no card names.
  const playing = tutorials.find((t) => t.tutorialId === playingId) ?? null;

  return (
    <div className={styles.root}>
      {playing && (
        <div ref={stageRef} className={styles.stage}>
          <div className={styles.player}>
            <iframe
              key={playing.tutorialId}
              src={youTubeEmbedUrl(playing.youtubeVideoId, { autoplay: true })}
              title={playing.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
          <div className={styles.stageHead}>
            <h3 className={styles.stageTitle}>{playing.title}</h3>
            <IconButton icon={X} label="Close video" size="sm" onClick={() => setPlayingId(null)} />
          </div>
        </div>
      )}

      <ul className={styles.grid}>
        {tutorials.map((t) => {
          const isPlaying = t.tutorialId === playing?.tutorialId;
          return (
            <li key={t.tutorialId}>
              <button
                type="button"
                className={styles.card}
                aria-pressed={isPlaying}
                data-playing={isPlaying || undefined}
                onClick={() => setPlayingId(t.tutorialId)}
              >
                <span className={styles.thumb}>
                  <img src={youTubeThumbnailUrl(t.youtubeVideoId)} alt="" loading="lazy" decoding="async" />
                  <span className={styles.play} aria-hidden="true">
                    {isPlaying ? <span className={styles.nowPlaying}>Playing</span> : <Icon icon={Play} variant="filled" size={20} />}
                  </span>
                </span>
                <span className={styles.caption}>
                  <span className={styles.title}>{t.title}</span>
                  {t.proOnly && <Tag tone="accent" size="sm">Pro</Tag>}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
